import { HttpFailure } from './http-failure';

/**
 * One shape for every failure a component or store has to show: the API's RFC 9457 problem details
 * when there are any, and something readable when there are not (a network failure, a proxy, an
 * empty body). Components never inspect an HttpErrorResponse.
 */
export interface ApiError {
  /** 0 when the request never reached the API. */
  readonly status: number;
  /** The API's stable machine-readable code (`context.holds_actions`, `admin.last`, …) when present. */
  readonly code: string | null;
  readonly title: string;
  readonly detail: string | null;
}

const UNREACHABLE: ApiError = {
  status: 0,
  code: 'api.unreachable',
  title: 'Cannot reach CompleteMe',
  detail: 'The server did not respond. Check that the API is running, then try again.',
};

export function toApiError(response: unknown): ApiError {
  if (!(response instanceof HttpFailure)) {
    return UNREACHABLE;
  }

  if (response.status === 0) {
    return UNREACHABLE;
  }

  const body = response.error as Record<string, unknown> | string | null;
  if (body && typeof body === 'object') {
    return {
      status: response.status,
      code: typeof body['code'] === 'string' ? body['code'] : null,
      title: typeof body['title'] === 'string' ? body['title'] : defaultTitle(response.status),
      detail: typeof body['detail'] === 'string' ? body['detail'] : null,
    };
  }

  return {
    status: response.status,
    code: null,
    title: defaultTitle(response.status),
    detail: typeof body === 'string' && body.length > 0 && body.length < 500 ? body : null,
  };
}

/**
 * Only for responses with no problem-details body. The API owns the wording of the ones that have a
 * body (its ErrorMapping is the authority); restating its vocabulary here would drift.
 */
function defaultTitle(status: number): string {
  if (status === 401) return 'Sign-in refused';
  return `The server returned ${status}`;
}

/**
 * `toApiError` for a request made with `responseType: 'blob'` — every CSV download this application
 * performs (020).
 *
 * The API answers a refusal with the same problem-details JSON body it always does, but a
 * blob-typed request hands that body back as a **Blob**: an object with none of the fields
 * `toApiError` reads. Without this, a 404 `tag.not_found` from a stale `/tags/:name` link reads as
 * "The server returned 404" while the server's own sentence sits unread inside the blob — the
 * failure looks like a broken download rather than a tag that no longer exists.
 *
 * The mapping itself is not restated: the body is parsed and handed back to `toApiError`.
 */
export async function toApiErrorFromBlob(response: unknown): Promise<ApiError> {
  if (response instanceof HttpFailure && response.error instanceof Blob) {
    try {
      const text = await response.error.text();
      return toApiError(new HttpFailure({
        error: text.length > 0 ? JSON.parse(text) : null,
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      }));
    } catch {
      // A blob that is not JSON is not a problem document; fall through to the plain reading, which
      // produces the status-only message this would otherwise have produced anyway.
    }
  }
  return toApiError(response);
}
